import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  CoupleProfile,
  Preferences,
  BudgetTarget,
  AccountStatistics,
  UpdateCoupleProfileRequest,
  UpdatePreferencesRequest,
  SetBudgetTargetsRequest,
  ExportDataRequest,
  DEFAULT_PREFERENCES,
  ChallengeDifficulty
} from '../../types/settings';
import { 
  updateCoupleProfile as apiUpdateCoupleProfile,
  updatePreferences as apiUpdatePreferences,
  setBudgetTargets as apiSetBudgetTargets,
  exportExpenseData as apiExportExpenseData,
  getAccountStatistics as apiGetAccountStatistics,
  downloadExportedFile
} from '../../lib/SettingsAndPreferences/api';

// Async thunks
export const updateCoupleProfile = createAsyncThunk(
  'settings/updateCoupleProfile',
  async ({ request, accessToken }: { request: UpdateCoupleProfileRequest; accessToken: string }) => {
    const result = await apiUpdateCoupleProfile(request, accessToken);
    if (!result.success) {
      throw new Error(result.error || 'Failed to update couple profile');
    }
    return result.data!;
  }
);

export const updatePreferences = createAsyncThunk(
  'settings/updatePreferences',
  async ({ request, accessToken }: { request: UpdatePreferencesRequest; accessToken: string }) => {
    const result = await apiUpdatePreferences(request, accessToken);
    if (!result.success) {
      throw new Error(result.error || 'Failed to update preferences');
    }
    return result.data!;
  }
);

export const setBudgetTargets = createAsyncThunk(
  'settings/setBudgetTargets',
  async ({ request, accessToken }: { request: SetBudgetTargetsRequest; accessToken: string }) => {
    const result = await apiSetBudgetTargets(request, accessToken);
    if (!result.success) {
      throw new Error(result.error || 'Failed to set budget targets');
    }
    return result.data!;
  }
);

export const exportExpenseData = createAsyncThunk(
  'settings/exportExpenseData',
  async ({ request, accessToken }: { request: ExportDataRequest; accessToken: string }) => {
    const result = await apiExportExpenseData(request, accessToken);
    if (!result.success) {
      throw new Error(result.error || 'Failed to export data');
    }
    
    // Automatically download the file
    downloadExportedFile(result.data!);
    
    return result.data!;
  }
);

export const getAccountStatistics = createAsyncThunk(
  'settings/getAccountStatistics',
  async ({ coupleId, accessToken }: { coupleId: string; accessToken: string }) => {
    const result = await apiGetAccountStatistics(coupleId, accessToken);
    if (!result.success) {
      throw new Error(result.error || 'Failed to get account statistics');
    }
    return result.data!;
  }
);

interface SettingsState {
  // Couple profile
  coupleProfile: CoupleProfile | null;
  isUpdatingProfile: boolean;
  
  // Preferences
  preferences: Preferences | null;
  isUpdatingPreferences: boolean;
  
  // Budget targets
  budgetTargets: BudgetTarget[];
  isUpdatingBudgets: boolean;
  
  // Account statistics
  accountStatistics: AccountStatistics | null;
  isLoadingStatistics: boolean;
  
  // Export functionality
  isExportingData: boolean;
  lastExportedAt: string | null;
  
  // Error handling
  error: string | null;
  
  // UI state
  hasUnsavedChanges: boolean;
}

const initialState: SettingsState = {
  coupleProfile: null,
  isUpdatingProfile: false,
  preferences: null,
  isUpdatingPreferences: false,
  budgetTargets: [],
  isUpdatingBudgets: false,
  accountStatistics: null,
  isLoadingStatistics: false,
  isExportingData: false,
  lastExportedAt: null,
  error: null,
  hasUnsavedChanges: false,
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Initialize settings for authenticated couple
    initializeSettings: (state, action: PayloadAction<{
      coupleProfile: CoupleProfile;
      preferences?: Preferences;
    }>) => {
      state.coupleProfile = action.payload.coupleProfile;
      
      // Initialize preferences with defaults if not provided
      if (action.payload.preferences) {
        state.preferences = action.payload.preferences;
      } else {
        state.preferences = {
          ...DEFAULT_PREFERENCES,
          couple_id: action.payload.coupleProfile.id,
        };
      }
    },
    
    // Local preference updates (before saving)
    updateLocalPreferences: (state, action: PayloadAction<Partial<Preferences>>) => {
      if (state.preferences) {
        state.preferences = { ...state.preferences, ...action.payload };
        state.hasUnsavedChanges = true;
      }
    },
    
    // Local budget updates (before saving)
    updateLocalBudgets: (state, action: PayloadAction<BudgetTarget[]>) => {
      state.budgetTargets = action.payload;
      state.hasUnsavedChanges = true;
    },
    
    // Mark changes as saved
    markChangesSaved: (state) => {
      state.hasUnsavedChanges = false;
    },
    
    // Reset settings (on logout)
    resetSettings: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Update couple profile
    builder
      .addCase(updateCoupleProfile.pending, (state) => {
        state.isUpdatingProfile = true;
        state.error = null;
      })
      .addCase(updateCoupleProfile.fulfilled, (state, action) => {
        state.isUpdatingProfile = false;
        state.coupleProfile = action.payload;
        state.hasUnsavedChanges = false;
        state.error = null;
      })
      .addCase(updateCoupleProfile.rejected, (state, action) => {
        state.isUpdatingProfile = false;
        state.error = action.error.message || 'Failed to update couple profile';
      });

    // Update preferences
    builder
      .addCase(updatePreferences.pending, (state) => {
        state.isUpdatingPreferences = true;
        state.error = null;
      })
      .addCase(updatePreferences.fulfilled, (state, action) => {
        state.isUpdatingPreferences = false;
        state.preferences = action.payload;
        state.hasUnsavedChanges = false;
        state.error = null;
      })
      .addCase(updatePreferences.rejected, (state, action) => {
        state.isUpdatingPreferences = false;
        state.error = action.error.message || 'Failed to update preferences';
      });

    // Set budget targets
    builder
      .addCase(setBudgetTargets.pending, (state) => {
        state.isUpdatingBudgets = true;
        state.error = null;
      })
      .addCase(setBudgetTargets.fulfilled, (state, action) => {
        state.isUpdatingBudgets = false;
        state.budgetTargets = action.payload;
        state.hasUnsavedChanges = false;
        state.error = null;
      })
      .addCase(setBudgetTargets.rejected, (state, action) => {
        state.isUpdatingBudgets = false;
        state.error = action.error.message || 'Failed to set budget targets';
      });

    // Export expense data
    builder
      .addCase(exportExpenseData.pending, (state) => {
        state.isExportingData = true;
        state.error = null;
      })
      .addCase(exportExpenseData.fulfilled, (state, action) => {
        state.isExportingData = false;
        state.lastExportedAt = new Date().toISOString();
        state.error = null;
      })
      .addCase(exportExpenseData.rejected, (state, action) => {
        state.isExportingData = false;
        state.error = action.error.message || 'Failed to export data';
      });

    // Get account statistics
    builder
      .addCase(getAccountStatistics.pending, (state) => {
        state.isLoadingStatistics = true;
        state.error = null;
      })
      .addCase(getAccountStatistics.fulfilled, (state, action) => {
        state.isLoadingStatistics = false;
        state.accountStatistics = action.payload;
        state.error = null;
      })
      .addCase(getAccountStatistics.rejected, (state, action) => {
        state.isLoadingStatistics = false;
        state.error = action.error.message || 'Failed to get account statistics';
      });
  },
});

export const {
  clearError,
  initializeSettings,
  updateLocalPreferences,
  updateLocalBudgets,
  markChangesSaved,
  resetSettings,
} = settingsSlice.actions;

export default settingsSlice.reducer;